import { useState, useEffect, useRef } from 'react'
import {
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Box,
  Chip,
} from '@mui/material'
import { Download, WhatsApp, Email } from '@mui/icons-material'
import { useStore } from '../store/useStore'
import { generatePDF, sendPDFByEmail } from '../utils/api'
import { DownloadModal } from './DownloadModal'

const EMAIL_STORAGE_KEY = 'lastUsedEmail'

export const ActionButtons = () => {
  const [emailDialogOpen, setEmailDialogOpen] = useState(false)
  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false)
  const [email, setEmail] = useState('')
  const [filename, setFilename] = useState('')
  const [loading, setLoading] = useState(false)
  const filenameInputRef = useRef<HTMLInputElement>(null)

  const { sessionId, files } = useStore()
  const selectedFiles = files.filter((f) => f.selected)
  const fileIds = selectedFiles.length > 0
    ? selectedFiles.sort((a, b) => (a.order || 0) - (b.order || 0)).map((f) => f.id)
    : files.map((f) => f.id)

  useEffect(() => {
    const savedEmail = localStorage.getItem(EMAIL_STORAGE_KEY)
    if (savedEmail) {
      setEmail(savedEmail)
    }
  }, [])

  useEffect(() => {
    if (emailDialogOpen && filenameInputRef.current && localStorage.getItem(EMAIL_STORAGE_KEY)) {
      filenameInputRef.current.focus()
    }
  }, [emailDialogOpen])

  const handleDownload = async (filename?: string) => {
    try {
      setLoading(true)
      const pdfBlob = await generatePDF(sessionId!, fileIds)
      const url = window.URL.createObjectURL(pdfBlob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename ? `${filename}.pdf` : 'whatsapp-images.pdf'
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (error) {
      console.error('Error downloading PDF:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleEmailSend = async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault()
    }
    
    if (!email) return

    setEmailDialogOpen(false)
    setFilename('')

    try {
      setLoading(true)
      localStorage.setItem(EMAIL_STORAGE_KEY, email)
      
      await sendPDFByEmail(sessionId!, {
        to: email,
        files: fileIds,
        filename: filename || undefined
      })
    } catch (error) {
      console.error('Error sending PDF via email:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleEmailSend()
    }
  }

  return (
    <>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{ mt: 4 }}
      >
        <Button
          variant="contained"
          startIcon={<Download />}
          onClick={() => setDownloadDialogOpen(true)}
          disabled={loading}
          fullWidth
          sx={{
            bgcolor: 'primary.main',
            '&:hover': { bgcolor: 'primary.dark' },
          }}
        >
          Download PDF
        </Button>
        <Button
          variant="contained"
          startIcon={<Email />}
          onClick={() => setEmailDialogOpen(true)}
          disabled={loading}
          fullWidth
          sx={{
            bgcolor: 'primary.main',
            '&:hover': { bgcolor: 'primary.dark' },
          }}
        >
          Send via Email
        </Button>
        <Box sx={{ position: 'relative', width: '100%' }}>
          <Button
            variant="contained"
            startIcon={<WhatsApp />}
            disabled={true}
            fullWidth
            sx={{
              bgcolor: 'primary.main',
              '&:hover': { bgcolor: 'primary.dark' },
              opacity: 0.7,
            }}
          >
            Send via WhatsApp
          </Button>
          <Chip
            label="Soon"
            color="primary"
            size="small"
            sx={{
              position: 'absolute',
              top: -10,
              right: -10,
              bgcolor: '#40C057',
              fontWeight: 'bold',
            }}
          />
        </Box>
      </Stack>

      <DownloadModal
        open={downloadDialogOpen}
        onClose={() => setDownloadDialogOpen(false)}
        onDownload={handleDownload}
      />

      <Dialog 
        open={emailDialogOpen} 
        onClose={() => {
          setEmailDialogOpen(false)
          setFilename('')
        }}
        maxWidth="sm"
        fullWidth
      >
        <form onSubmit={handleEmailSend}>
          <DialogTitle>Send PDF via Email</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                autoFocus={!email}
                label="Email Address"
                type="email"
                fullWidth
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                onKeyDown={handleKeyDown}
              />
              <TextField
                inputRef={filenameInputRef}
                label="Filename (optional)"
                fullWidth
                value={filename}
                onChange={(e) => setFilename(e.target.value)}
                onKeyDown={handleKeyDown}
                InputProps={{
                  endAdornment: '.pdf'
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => {
                setEmailDialogOpen(false)
                setFilename('')
              }}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              disabled={!email || loading}
            >
              Send
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </>
  )
} 